import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

const navCreditsIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const navCreditsPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const BORDER = 'var(--hd-border)';
const FOREGROUND = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED_BG = 'var(--hd-muted)';
const CARD = 'var(--hd-card)';
const TOPBAR_BG = 'color-mix(in oklab, var(--hd-background) 82%, transparent)';
const GRADIENT = 'var(--hd-gradient-houdiny)';
const HOUDINY_FROM = 'var(--hd-houdiny-from)';
const SHADOW_ELEVATED = 'var(--hd-shadow-elevated)';

/* Radius scale mirrors the Remix reference (--radius: 0.875rem):
   md 12px · lg 14px · xl 18px · 2xl 22px */

export const StyledTopBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  border-bottom: 1px solid ${BORDER};
  background: ${TOPBAR_BG};
  backdrop-filter: blur(20px);

  .trial-banner-visible & {
    top: 42px;
  }

  .topbar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    max-width: 1480px;
    height: 64px;
    margin: 0 auto;
    padding: 0 32px;

    @media (max-width: 767px) {
      padding: 0 16px;
    }
  }

  .brand {
    display: flex;
    flex-shrink: 0;
    align-items: center;

    img {
      height: 32px;
      width: auto;
    }
  }

  .topbar-nav {
    display: none;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      height: 0;
    }

    @media (min-width: 992px) {
      display: flex;
    }
  }

  .nav-item {
    flex-shrink: 0;
    padding: 6px 12px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: ${MUTED_FG};
    text-decoration: none;
    transition: color 0.15s ease, background 0.15s ease;

    &:hover {
      color: ${FOREGROUND};
    }

    &.active {
      color: ${FOREGROUND};
      background: ${MUTED_BG};
    }
  }

  .topbar-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }

  /* AI Home credits: a quiet pill, set apart from the profile button. */
  .nav-credits {
    ${brandTheme}
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    margin-right: 14px;
    padding: 0 14px 0 12px;
    border: 1px solid var(--hd-border);
    border-radius: 999px;
    background: var(--hd-background);
    white-space: nowrap;
    cursor: default;
    transition:
      border-color 0.15s ease,
      background 0.15s ease;

    &:hover {
      border-color: color-mix(in srgb, var(--hd-foreground) 18%, var(--hd-border));
      background: color-mix(in srgb, var(--hd-muted) 40%, var(--hd-background));
    }

    .nav-credits-icon {
      display: grid;
      flex-shrink: 0;
      place-items: center;
      color: var(--hd-foreground);

      svg {
        width: 15px;
        height: 15px;
        stroke-width: 2.25;
        /* Lucide's coins face left; mirrored so they face right. */
        transform: scaleX(-1);
      }
    }

    .nav-credits-value {
      font-size: 13px;
      font-weight: 600;
      line-height: 1;
      color: var(--hd-foreground);
      font-variant-numeric: tabular-nums;
      /* Eases in when the balance arrives, in place of the placeholder. */
      animation: ${navCreditsIn} 220ms ease-out both;
    }

    /* Holds the number's place while user-details loads: same height as the
       text, roughly a balance's width, so nothing shifts when it lands. */
    .nav-credits-placeholder {
      display: inline-block;
      width: 26px;
      height: 10px;
      border-radius: 999px;
      background: var(--hd-muted);
      animation: ${navCreditsPulse} 1.2s ease-in-out infinite;
    }

    &.is-loading {
      cursor: progress;
    }

    @media (prefers-reduced-motion: reduce) {
      .nav-credits-value,
      .nav-credits-placeholder {
        animation: none;
      }
    }

    .nav-credits-label {
      margin-left: -4px;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      color: var(--hd-muted-foreground);
    }
  }

  .search-btn {
    display: none;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-size: 14px;
    color: ${MUTED_FG};
    background: ${CARD};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED_BG};
    }

    kbd {
      margin-left: 8px;
      padding: 2px 6px;
      border: 1px solid ${BORDER};
      border-radius: 6px;
      font-family: inherit;
      font-size: 10px;
      font-weight: 500;
      background: ${MUTED_BG};
    }

    @media (min-width: 992px) {
      display: inline-flex;
    }
  }

  .icon-btn {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 14px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      color: ${FOREGROUND};
      background: ${MUTED_BG};
    }

    img {
      width: 18px;
      height: 18px;
    }
  }

  .mobile-nav-btn {
    @media (min-width: 992px) {
      display: none;
    }
  }

  .bell-wrap {
    position: relative;
  }

  /* Notifications molecule is absolutely positioned by its own styles; this
     wrapper only needs to be the positioning context and stay above content. */
  .notification-panel {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    z-index: 70;
    display: none;

    &.visible {
      display: block;
    }
  }
`;

export const StyledMobileNav = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 16px;
  right: 16px;
  z-index: 70;
  max-height: 70vh;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};
  box-shadow: ${SHADOW_ELEVATED};

  @media (min-width: 992px) {
    display: none;
  }

  a {
    display: block;
    padding: 10px 12px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    color: ${FOREGROUND};
    text-decoration: none;

    &:hover {
      background: ${MUTED_BG};
    }

    &.active {
      color: ${FOREGROUND};
      background: ${MUTED_BG};
    }
  }
`;

/** Empty-state avatar uses the solid brand colour (not brand gradient). Photo covers it when present. */
export const AvatarButton = styled.button`
  ${brandTheme}
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  margin-left: 4px;
  padding: 0;
  overflow: hidden;
  border: none;
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${CARD};
  background: var(--hd-primary) !important;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const StyledProfileMenu = styled.div`
  position: relative;

  .profile-popover {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    z-index: 70;
    width: 320px;
    max-height: calc(100vh - 96px);
    overflow-y: auto;

    /*
     * Setting one axis to auto makes the other auto as well — that is what the
     * spec does with a computed "visible" beside a non-visible value — so a child
     * overflowing by a pixel drew a horizontal scrollbar across the bottom of the
     * popover. Nothing in here is meant to scroll sideways, so the axis is closed
     * rather than the stray pixel chased down.
     */
    overflow-x: hidden;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
    box-shadow: ${SHADOW_ELEVATED};
  }

  .popover-divider {
    height: 1px;
    background: ${BORDER};
  }

  .popover-head {
    padding: 20px 20px 16px;

    .user-name {
      font-size: 16px;
      font-weight: 600;
      color: ${FOREGROUND};
    }

    .user-email {
      margin-top: 2px;
      font-size: 14px;
      color: ${MUTED_FG};
      overflow-wrap: anywhere;
    }

    .plan-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 16px;
      font-size: 14px;

      .plan-name {
        font-weight: 500;
        color: ${FOREGROUND};
      }

      .plan-sep {
        color: ${MUTED_FG};
      }

      .plan-status {
        font-weight: 500;
        color: #10b981;
      }

      .plan-status.inactive {
        color: var(--hd-destructive);
      }
    }
  }

  /* Reference: the "workspace · role" line under the email. */
  .user-context {
    margin-top: 4px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  /* Reference: the Workspace switcher block above the nav items. */
  .workspace-block {
    padding: 12px 20px;

    .workspace-title {
      margin-bottom: 8px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${MUTED_FG};
    }

    .workspace-list {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .workspace-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      padding: 6px 8px;
      border: none;
      border-radius: 14px;
      font-size: 14px;
      text-align: left;
      color: ${MUTED_FG};
      background: transparent;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &:hover {
        color: ${FOREGROUND};
        background: color-mix(in srgb, ${MUTED_BG} 60%, transparent);
      }

      &.active {
        color: ${FOREGROUND};
        background: ${MUTED_BG};
        cursor: default;
      }

      svg {
        flex-shrink: 0;
        color: var(--hd-primary);
      }
    }
  }

  /* Reference: the footer pairs Logout with a language switch. */
  .popover-foot-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .lang-switch {
    display: flex;
    align-items: center;
    padding: 2px;
    border: 1px solid ${BORDER};
    border-radius: 14px;

    button {
      display: grid;
      place-items: center;
      width: 36px;
      height: 28px;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      background: transparent;
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover {
        background: color-mix(in srgb, ${MUTED_BG} 60%, transparent);
      }

      &.active {
        background: ${MUTED_BG};
      }
    }
  }

  .credits-block {
    padding: 16px 20px;

    .credits-title {
      font-size: 14px;
      font-weight: 600;
      color: ${FOREGROUND};
    }
  }

  .credit-row {
    margin-top: 12px;

    .credit-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      font-size: 12px;
      color: ${MUTED_FG};
    }

    .credit-track {
      height: 6px;
      margin-top: 6px;
      overflow: hidden;
      border-radius: 999px;
      background: ${MUTED_BG};
    }

    /* The brand orange-to-dark gradient, as version3's AI Credits bar. The popover
       sits outside the brand theme, where the gradient token is plain black. */
    .credit-fill {
      ${brandTheme}
      height: 100%;
      border-radius: 999px;
      background: ${GRADIENT};
      transition: width 0.3s ease;
    }
  }

  .popover-nav {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
  }

  .popover-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 20px;
    border: none;
    font-size: 14px;
    text-align: left;
    color: ${FOREGROUND};
    text-decoration: none;
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED_BG};
    }

    svg {
      flex-shrink: 0;
      color: ${MUTED_FG};
    }
  }

  .theme-block {
    padding: 12px 20px;

    .theme-title {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${MUTED_FG};
    }

    .theme-switch {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      margin-top: 8px;
      padding: 4px;
      border: 1px solid ${BORDER};
      border-radius: 18px;
    }

    .theme-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 8px;
      border: none;
      border-radius: 14px;
      font-size: 11px;
      font-weight: 500;
      color: ${MUTED_FG};
      background: transparent;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;

      &:hover {
        color: ${FOREGROUND};
        background: ${MUTED_BG};
      }

      &.active {
        color: ${FOREGROUND};
        background: ${MUTED_BG};
      }
    }
  }

  .popover-foot {
    padding: 12px 20px;

    .logout-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 0;
      border: none;
      font-size: 14px;
      font-weight: 500;
      color: var(--hd-destructive);
      background: transparent;
      cursor: pointer;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .upgrade-card {
    margin: 0 12px 12px;
    padding: 12px;
    border-radius: 18px;
    background: color-mix(in srgb, ${MUTED_BG} 50%, transparent);

    .upgrade-inner {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .upgrade-icon {
      display: grid;
      flex-shrink: 0;
      place-items: center;
      width: 40px;
      height: 40px;
      border-radius: 14px;
      color: #fff;
      background: ${GRADIENT};
    }

    .upgrade-title {
      font-size: 13px;
      font-weight: 600;
      line-height: 1.25;
      color: ${FOREGROUND};
    }

    .upgrade-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;
      padding: 0;
      border: none;
      font-size: 13px;
      font-weight: 500;
      background: transparent;
      cursor: pointer;

      .upgrade-btn-text {
        color: #000;
        -webkit-text-fill-color: #000;
      }

      svg {
        color: ${HOUDINY_FROM};
      }
    }
  }
`;
